import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatDate, getCurrentStateBadge } from '../../utils/utils'

import { useCandidateTimeline } from './useCandidateTimeline'
import type { TimelineItem } from './candidateTimeline.utils'
import styles from './CandidateTimeline.module.scss'
import { Badge, ButtonText, CardDashboard, Flex, IllustratedMessage, Loader, Text, theme } from '@percent/lemonade'
import { HistoryListItem } from '@percent/partner-dashboard/common/components/historyListItem/HistoryListItem'

export type CandidateTimelineProps = Readonly<{
  programId: string
  candidateId: string
}>

type EntryProps<Kind extends TimelineItem['kind']> = Readonly<{ item: Extract<TimelineItem, { kind: Kind }> }>

function StateBadge({ state }: Readonly<{ state: string }>) {
  const { t } = useTranslation()
  const { label, variant, icon } = getCurrentStateBadge({ currentState: state, t })

  return (
    <Badge variant={variant} icon={icon} size="small">
      {label}
    </Badge>
  )
}

function StatusEntry({ item }: EntryProps<'status'>) {
  const [checksShown, setChecksShown] = useState(false)

  return (
    <>
      <Flex align="center" gap={theme.spacing.xs} wrap="wrap" data-testid="timeline-transition">
        {item.from && (
          <>
            <StateBadge state={item.from} />
            <Text as="span" color={theme.semantic.contentTertiary}>
              →
            </Text>
          </>
        )}
        <StateBadge state={item.to} />
      </Flex>
      <Text size="small" color={theme.semantic.contentSecondary}>
        {item.reason}
      </Text>
      {item.checks && (
        <>
          <Flex>
            <ButtonText
              icon={checksShown ? 'chevron-up' : 'chevron-down'}
              onClick={() => setChecksShown(shown => !shown)}
              aria-expanded={checksShown}
            >
              {item.checks.summary}
            </ButtonText>
          </Flex>
          {checksShown && (
            <Flex direction="column" gap={theme.spacing.xxs} className={styles.checks} data-testid="timeline-checks">
              {item.checks.items.map(check => (
                <Text key={`${check.label}-${check.value}`} size="small" color={theme.semantic.contentSecondary}>
                  {`${check.label}: ${check.value}`}
                </Text>
              ))}
            </Flex>
          )}
        </>
      )}
    </>
  )
}

function ChangeEntry({ item }: EntryProps<'change'>) {
  return (
    <>
      {item.changes.map(change => (
        <Text key={change.label}>
          <Text as="span" color={theme.semantic.contentSecondary}>{`${change.label}: `}</Text>
          {change.previous && <Text as="span" color={theme.semantic.contentTertiary}>{`${change.previous} → `}</Text>}
          <Text as="span" fontWeight="semiBold">
            {change.next}
          </Text>
        </Text>
      ))}
    </>
  )
}

function EmailEntry({ item }: EntryProps<'email'>) {
  const { t } = useTranslation()

  return (
    <>
      <Text>{item.subject}</Text>
      <Text size="small" color={theme.semantic.contentSecondary}>
        {t('grants.candidateDetails.timeline.email.sentTo', { recipient: item.recipient })}
      </Text>
    </>
  )
}

function TimelineEntry({ item }: Readonly<{ item: TimelineItem }>) {
  return (
    <HistoryListItem>
      <Flex direction="column" gap={theme.spacing.xxs} mb={theme.spacing.l} w="100%" data-testid="timeline-entry">
        <Flex justify="space-between" align="baseline" gap={theme.spacing.m}>
          <Text fontWeight="semiBold">{item.title}</Text>
          <Text size="small" color={theme.semantic.contentTertiary}>
            {formatDate(item.occurredAt)}
          </Text>
        </Flex>
        {item.kind === 'status' && <StatusEntry item={item} />}
        {item.kind === 'change' && <ChangeEntry item={item} />}
        {item.kind === 'email' && <EmailEntry item={item} />}
      </Flex>
    </HistoryListItem>
  )
}

export function CandidateTimeline({ programId, candidateId }: CandidateTimelineProps) {
  const { t } = useTranslation()
  const { items, isLoading } = useCandidateTimeline({ programId, candidateId })

  if (isLoading) {
    return <Loader />
  }

  return (
    <CardDashboard title={t('grants.candidateDetails.timeline.title')} data-testid="candidate-timeline">
      {items.length > 0 ? (
        items.map(item => <TimelineEntry key={item.id} item={item} />)
      ) : (
        <IllustratedMessage
          title={t('grants.candidateDetails.timeline.empty.title')}
          description={t('grants.candidateDetails.timeline.empty.description')}
          illustration="no-grant-candidates"
        />
      )}
    </CardDashboard>
  )
}
